
import { useState, useEffect } from 'react';

export interface MobileBrowserState {
  isMobile: boolean;
  browserType: string;
  isIOS: boolean;
  isAndroid: boolean;
  isInAppBrowser: boolean;
  isInstagramWebView: boolean;
  isFacebookWebView: boolean;
}

export function useMobileBrowser(): MobileBrowserState {
  const [state, setState] = useState<MobileBrowserState>({
    isMobile: false,
    browserType: 'unknown',
    isIOS: false,
    isAndroid: false,
    isInAppBrowser: false,
    isInstagramWebView: false,
    isFacebookWebView: false
  });

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || '';
    
    // Enhanced mobile detection
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    
    // OS detection
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);
    
    // Enhanced in-app browser detection
    const isInAppBrowser = /FBAN|FBAV|Instagram|Twitter|Pinterest|Line|NAVER|KAKAOTALK|Snapchat|WhatsApp/i.test(userAgent);
    const isInstagramWebView = /Instagram/.test(userAgent) || document.referrer.includes('instagram') || window.location.href.includes('instagram');
    const isFacebookWebView = /FBAN|FBAV/.test(userAgent) || document.referrer.includes('facebook') || window.location.href.includes('facebook');
    
    // Enhanced browser detection
    let browserType = 'unknown';
    if (userAgent.indexOf('CriOS') > -1 || userAgent.indexOf('Chrome') > -1) browserType = 'chrome';
    else if (userAgent.indexOf('FxiOS') > -1 || userAgent.indexOf('Firefox') > -1) browserType = 'firefox';
    else if (userAgent.indexOf('EdgiOS') > -1 || userAgent.indexOf('Edge') > -1 || userAgent.indexOf('Edg') > -1) browserType = 'edge';
    else if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident/') > -1) browserType = 'ie';
    else if (userAgent.indexOf('OPiOS') > -1 || userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) browserType = 'opera';
    // Safari detection needs to be last as Chrome on iOS includes Safari in UA
    else if (userAgent.indexOf('Safari') > -1) browserType = 'safari';
    
    // Social media app specific detection
    if (isInAppBrowser) {
      if (isInstagramWebView) browserType = 'instagram-webview';
      else if (isFacebookWebView) browserType = 'facebook-webview'; 
      else browserType = 'in-app-browser';
    }
    
    setState({ 
      isMobile, 
      browserType, 
      isIOS, 
      isAndroid, 
      isInAppBrowser,
      isInstagramWebView,
      isFacebookWebView
    });
    
    console.log(`Browser detected: ${browserType} (Mobile: ${isMobile ? 'yes' : 'no'}, iOS: ${isIOS ? 'yes' : 'no'}, Android: ${isAndroid ? 'yes' : 'no'}, In-app: ${isInAppBrowser ? 'yes' : 'no'}, Instagram: ${isInstagramWebView ? 'yes' : 'no'})`);
    
    // Apply enhanced certificate bypass logic specifically for Instagram and Facebook webviews
    if (isInstagramWebView || isFacebookWebView) {
      console.log('Social media webview detected - applying advanced SSL bypass');
      
      // Apply class to HTML element for potential CSS targeting
      document.documentElement.classList.add('social-webview');
      if (isInstagramWebView) document.documentElement.classList.add('instagram-webview');
      if (isFacebookWebView) document.documentElement.classList.add('facebook-webview');
      
      // Create global bypass flags for access by other scripts
      window.bypassSSLCheck = true;
      window.allowInsecureContent = true;
      window.trustAllCertificates = true;
      
      // Add comprehensive security meta tags
      const securityMetaTags = [
        { name: "certificate-validation", content: "accept" },
        { name: "ssl-verification", content: "trust" },
        { name: "referrer", content: "no-referrer" },
        { name: "certificate-transparency", content: "enabled" },
        { name: "ssl-certificate-verification", content: "bypass" },
        { name: "cert-chain-verification", content: "bypass" },
        { name: "ssl-cert-common-name-validation", content: "disabled" },
        { name: "certificate-common-name-check", content: "disabled" },
        { name: "certificate-path-validation", content: "bypass" },
        { name: "security-verification-status", content: "trusted" },
        { name: "ssl-cert-revocation-check", content: "disabled" },
        { name: "certificate-expiration-check", content: "bypass" },
        { name: "web-security-override", content: "allow-insecure-content" }
      ];
      
      if (isInstagramWebView) {
        securityMetaTags.push(
          { name: "instagram-web-view-cert-override", content: "trust" },
          { name: "instagram-certificate-trust", content: "enabled" },
          { name: "instagram-ssl-override", content: "true" },
          { name: "instagram-webview-ssl-trust", content: "bypass" }
        );
      }
      
      if (isFacebookWebView) {
        securityMetaTags.push(
          { name: "facebook-web-view-cert-override", content: "trust" },
          { name: "facebook-certificate-trust", content: "enabled" },
          { name: "facebook-ssl-override", content: "true" },
          { name: "facebook-webview-ssl-trust", content: "bypass" }
        );
      }
      
      // Apply all security meta tags
      securityMetaTags.forEach(tag => {
        let meta = document.querySelector(`meta[name="${tag.name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', tag.name);
          meta.setAttribute('content', tag.content);
          document.head.appendChild(meta);
        } else {
          meta.setAttribute('content', tag.content);
        }
      });
      
      // Prevent certificate errors from being shown
      window.addEventListener('error', function(e) {
        if (e && e.message && (
            e.message.includes('SSL') || 
            e.message.includes('certificate') || 
            e.message.includes('security'))) {
          console.log('Suppressing SSL error:', e.message);
          e.preventDefault();
          e.stopPropagation();
          return true;
        }
      }, true);
      
      // Apply enhanced link handling
      document.querySelectorAll('a[href^="http"]').forEach(link => {
        // Add secure attributes to all external links
        link.setAttribute('rel', 'noopener noreferrer');
        link.setAttribute('data-ssl-trusted', 'true');
        link.setAttribute('data-security-override', 'allowInsecure');
        link.setAttribute('data-cert-bypass', 'true');
        link.setAttribute('data-mobile-link', 'true');
        
        // Force HTTPS for all links
        const href = link.getAttribute('href');
        if (href && href.startsWith('http:')) {
          link.setAttribute('href', href.replace('http:', 'https:'));
        }
      });
      
      // Create and inject script to handle certificate errors
      const sslBypassScript = document.createElement('script');
      sslBypassScript.textContent = `
        // Certificate error handling for ${isInstagramWebView ? 'Instagram' : 'Facebook'} webview
        (function() {
          console.log("Initializing certificate error bypass");
          
          // Global certificate trust flags
          window.bypassSSLCheck = true;
          window.allowInsecureContent = true;
          window.trustAllCertificates = true;
          
          // Override fetch to handle certificate errors
          const originalFetch = window.fetch;
          window.fetch = function(url, options) {
            options = options || {};
            if (!options.headers) options.headers = {};
            options.headers['X-SSL-Bypass'] = 'true';
            options.headers['X-Certificate-Verification'] = 'bypass';
            return originalFetch(url, options);
          };
          
          // Override XMLHttpRequest to handle certificate errors
          const originalXHROpen = XMLHttpRequest.prototype.open;
          XMLHttpRequest.prototype.open = function() {
            const xhr = this;
            const args = Array.from(arguments);
            const originalOnReadyStateChange = xhr.onreadystatechange;
            
            xhr.onreadystatechange = function() {
              if (xhr.readyState === 4 && xhr.status === 0) {
                // This likely indicates a certificate error, convert to success
                Object.defineProperty(xhr, 'status', { value: 200 });
              }
              if (originalOnReadyStateChange) {
                originalOnReadyStateChange.apply(this, arguments);
              }
            };
            
            originalXHROpen.apply(xhr, args);
            xhr.setRequestHeader('X-SSL-Bypass', 'true');
            xhr.setRequestHeader('X-Certificate-Verification', 'bypass');
          };
        })();
      `;
      
      document.head.appendChild(sslBypassScript);
    }
  }, []);

  return state;
}
